/**
 * Search Relevance Engine v1
 * P20250925-113 — Lightweight fuzzy search with synonyms and ranking boosts
 */

import { fuzzyScore, levenshteinDistance } from '@/lib/fuzzySearch';
import synonymsData from '@/content/search/synonyms.json';

// Synonym map for query expansion
const SYNONYMS = new Map<string, string[]>(
  Object.entries(synonymsData as Record<string, string[]>),
);

// Scoring weights
const WEIGHTS = {
  EXACT_MATCH: 100,
  STARTS_WITH: 80,
  CONTAINS: 60,
  FUZZY_MATCH: 40,
  TAG_MATCH: 30,
  SYNONYM_MATCH: 25,
  TLD_PREMIUM: 15, // .com, .io, .ai boost
  TLD_STANDARD: 5,
  PRICE_PREMIUM: 10, // $10k+ domains
  PRICE_MID: 5, // $1k-$10k
  PRICE_LOW: 2, // <$1k
};

export interface SearchableItem {
  domain: string;
  tags?: string | string[] | null;
  tld?: string | null;
  binPrice?: number | null;
  length?: number | null;
}

export interface ScoredResult<T extends SearchableItem> {
  item: T;
  score: number;
  matchType: 'exact' | 'prefix' | 'fuzzy' | 'tag' | 'synonym' | 'none';
  highlighted: string;
}

/**
 * Tokenize query into searchable terms
 */
function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .trim()
    .split(/[\s\-_.]+/)
    .filter((t) => t.length > 0);
}

/**
 * Expand query with synonyms
 */
function expandWithSynonyms(tokens: string[]): string[] {
  const expanded = new Set<string>(tokens);

  for (const token of tokens) {
    const syns = SYNONYMS.get(token.toLowerCase());
    if (syns) {
      syns.forEach((s) => expanded.add(s.toLowerCase()));
    }
  }

  return Array.from(expanded);
}

/**
 * Get TLD boost score
 */
function getTldBoost(tld: string | null | undefined): number {
  if (!tld) return 0;
  const normalized = tld.toLowerCase().replace(/^\./, '');

  const premium = ['com', 'io', 'ai', 'app'];
  const standard = ['net', 'co', 'org', 'tech', 'dev'];

  if (premium.includes(normalized)) return WEIGHTS.TLD_PREMIUM;
  if (standard.includes(normalized)) return WEIGHTS.TLD_STANDARD;
  return 0;
}

/**
 * Get price band boost score
 */
function getPriceBoost(price: number | null | undefined): number {
  if (!price) return 0;

  if (price >= 10000) return WEIGHTS.PRICE_PREMIUM;
  if (price >= 1000) return WEIGHTS.PRICE_MID;
  return WEIGHTS.PRICE_LOW;
}

/**
 * Normalize tags to array
 */
function normalizeTags(tags: string | string[] | null | undefined): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    return tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Score a single domain against query tokens
 */
function scoreItem<T extends SearchableItem>(
  item: T,
  queryTokens: string[],
  expandedTokens: string[],
): ScoredResult<T> {
  const domain = item.domain.toLowerCase();
  const domainTokens = tokenize(domain);
  const tags = normalizeTags(item.tags).map((t) => t.toLowerCase());

  let maxScore = 0;
  let matchType: ScoredResult<T>['matchType'] = 'none';
  let bestMatchToken = '';

  // Check each expanded token against domain
  for (const token of expandedTokens) {
    // Exact domain match
    if (domain === token) {
      const score = WEIGHTS.EXACT_MATCH + getTldBoost(item.tld) + getPriceBoost(item.binPrice);
      if (score > maxScore) {
        maxScore = score;
        matchType = 'exact';
        bestMatchToken = token;
      }
    }

    // Starts with
    if (domain.startsWith(token)) {
      const score = WEIGHTS.STARTS_WITH + getTldBoost(item.tld) + getPriceBoost(item.binPrice);
      if (score > maxScore) {
        maxScore = score;
        matchType = 'prefix';
        bestMatchToken = token;
      }
    }

    // Contains substring
    if (domain.includes(token)) {
      const score = WEIGHTS.CONTAINS + getTldBoost(item.tld);
      if (score > maxScore) {
        maxScore = score;
        matchType = 'prefix';
        bestMatchToken = token;
      }
    }

    // Fuzzy matching for typos (only for tokens >= 4 chars)
    if (token.length >= 4) {
      for (const domainToken of domainTokens) {
        if (domainToken.length >= 3) {
          const distance = levenshteinDistance(token, domainToken);
          if (distance <= 2) {
            const score = WEIGHTS.FUZZY_MATCH - distance * 10 + getTldBoost(item.tld);
            if (score > maxScore) {
              maxScore = score;
              matchType = 'fuzzy';
              bestMatchToken = token;
            }
          }
        }
      }
    }

    // Tag matching
    for (const tag of tags) {
      if (tag === token) {
        const score = WEIGHTS.TAG_MATCH;
        if (score > maxScore) {
          maxScore = score;
          matchType = 'tag';
          bestMatchToken = token;
        }
      }

      // Fuzzy tag matching
      if (token.length >= 4 && tag.length >= 3) {
        const distance = levenshteinDistance(token, tag);
        if (distance <= 1) {
          const score = WEIGHTS.TAG_MATCH - distance * 5;
          if (score > maxScore) {
            maxScore = score;
            matchType = 'tag';
            bestMatchToken = token;
          }
        }
      }
    }

    // Synonym bonus (if this token came from synonym expansion)
    if (!queryTokens.includes(token) && expandedTokens.includes(token)) {
      if (domain.includes(token)) {
        const score = WEIGHTS.SYNONYM_MATCH;
        if (score > maxScore) {
          maxScore = score;
          matchType = 'synonym';
          bestMatchToken = token;
        }
      }
    }
  }

  return {
    item,
    score: maxScore,
    matchType,
    highlighted: bestMatchToken ? highlightMatch(item.domain, bestMatchToken) : item.domain,
  };
}

/**
 * Simple highlighting for matched terms
 */
function highlightMatch(text: string, query: string): string {
  if (!query) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  return (
    text.substring(0, index) +
    '<mark class="bg-primary/20 text-primary font-medium">' +
    text.substring(index, index + query.length) +
    '</mark>' +
    text.substring(index + query.length)
  );
}

/**
 * Main search function with relevance scoring
 */
export function search<T extends SearchableItem>(
  items: T[],
  query: string,
  options: {
    minScore?: number;
    maxResults?: number;
  } = {},
): ScoredResult<T>[] {
  const { minScore = 0, maxResults } = options;

  if (!query || !query.trim()) {
    return items.map((item) => ({
      item,
      score: 0,
      matchType: 'none' as const,
      highlighted: item.domain,
    }));
  }

  const startTime = performance.now();

  // Tokenize and expand query
  const queryTokens = tokenize(query);
  const expandedTokens = expandWithSynonyms(queryTokens);

  // Score all items
  const results = items
    .map((item) => scoreItem(item, queryTokens, expandedTokens))
    .filter((result) => result.score >= minScore)
    .sort((a, b) => b.score - a.score);

  const latency = performance.now() - startTime;

  // Log performance if slow
  if (latency > 100) {
    console.warn(`[Search] Slow search: ${latency.toFixed(1)}ms for ${items.length} items`);
  }

  return maxResults ? results.slice(0, maxResults) : results;
}

/**
 * Get search statistics for analytics
 */
export function getSearchStats<T extends SearchableItem>(
  results: ScoredResult<T>[],
  query: string,
  latencyMs: number,
): {
  qLen: number;
  results: number;
  latencyMs: number;
  avgScore: number;
  matchTypes: Record<string, number>;
} {
  const matchTypes: Record<string, number> = {};
  let totalScore = 0;

  for (const result of results) {
    matchTypes[result.matchType] = (matchTypes[result.matchType] || 0) + 1;
    totalScore += result.score;
  }

  return {
    qLen: query.trim().length,
    results: results.length,
    latencyMs: Math.round(latencyMs),
    avgScore: results.length > 0 ? Math.round(totalScore / results.length) : 0,
    matchTypes,
  };
}
